import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxiosResponse } from 'axios';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import axios from '@/_utils/axios';
import { selectIsPlayerOpened } from '@/redux/selectors/packagingSelectors';
import { selectTimelineOverlays, selectTimelineSegments } from '@/redux/selectors/packagingTimelineSelectors';
import type { DraggableId, Source, TimelineItem } from '@/redux/slices/packagingSlice.types';
import { resetAllPreviewUrls, setPreviewUrls } from '@/redux/slices/packagingTimelineSlice';
import { areAllAssignedItemsPreviewsLoaded } from '@/components/Packaging/utils';
import { isAssignedOverlay, isSegment, isTimelineItemAssigned } from '@/components/Packaging/typeGuards';

interface Props {
  children(isLoading: boolean, retryLoadingFailedPreview: () => void): React.ReactNode;
}

// In case if date provided by server is not explicitly in UTC format (doesn't have a "Z" character in the end of string)
//  then add "Z" to make sure date will represent a UTC time when parsed.
//  More details in a slack thread: https://tmt-insights.slack.com/archives/C062KDCEBGX/p1758368182447849?thread_ts=1758314454.818949&cid=C062KDCEBGX
const enforceUtcFormat = (str: string): string => {
  return str.replace(/z$/i, '') + 'Z';
};

const getItemSource = (timelineItem: TimelineItem) => {
  return isSegment(timelineItem) ? timelineItem.source : isAssignedOverlay(timelineItem) ? timelineItem.source : undefined;
};

type PreviewURLsBySegmentId = Record<DraggableId, Pick<Source, 'previewUrl' | 'previewLoadingError'> & { expirationDate?: string }>;

const fetchPreviewURLsUtil = async (items: TimelineItem[]) => {
  const handleResponse = (
    itemId: DraggableId,
    res: AxiosResponse<{ success: boolean; message?: string; detail?: string; data?: { url: string; expiration_date: string } }>
  ) => {
    if (!res.data.success) {
      const errorMessage = res.data.detail || res.data.message || 'Failed to load preview';
      previewsByItemId[itemId] = { previewLoadingError: { reason: errorMessage } };
    } else if (res.data.data?.url) {
      previewsByItemId[itemId] = { previewUrl: res.data.data.url, expirationDate: enforceUtcFormat(res.data.data.expiration_date) };
    }
  };

  const previewsByItemId: PreviewURLsBySegmentId = {};

  await Promise.allSettled(
    items.map((timelineItem) => {
      const source = getItemSource(timelineItem);
      if (!source || source.previewUrl) return; // do nothing if timelineItem has no assigned inventory, or `previewUrl` is already fetched

      return axios
        .get(`/inventory/titles/${source.titleId}/files/${source.proxyInventoryId ?? source.inventoryId}/`)
        .then((res) => handleResponse(timelineItem.draggableId, res))
        .catch((err) => handleResponse(timelineItem.draggableId, err));
    })
  );

  return previewsByItemId;
};

const extractExpirationDates = (map: Record<any, { expirationDate?: string }>) => {
  return Object.entries(map).flatMap(([, { expirationDate }]) => (expirationDate ? [expirationDate] : []));
};

const sortDatesAsc = (dates: Date[]) => dates.sort((a, b) => a.getTime() - b.getTime());

/**
 * The purpose of this component is to load preview URLs for all segments and overlays
 *  and provide a function to reload failed previews.
 */
const PreviewUrlLoader: React.FC<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isPlayerOpened = useAppSelector(selectIsPlayerOpened);
  const segments = useAppSelector(selectTimelineSegments);
  const overlayLayers = useAppSelector(selectTimelineOverlays);
  const [isLoading, setIsLoading] = useState(false);
  const resetPreviewUrlsTimer = useRef<ReturnType<typeof setTimeout>>();
  const closestExpirationDate = useRef<Date | undefined>();

  const overlaysFlat = useMemo(() => overlayLayers.flat(), [overlayLayers]);

  const setResetPreviewsTimeout = useCallback((previewURLsBySegmentId: PreviewURLsBySegmentId) => {
    const expirationDateStrings = extractExpirationDates(previewURLsBySegmentId);
    const expirationDates = expirationDateStrings.map((date) => new Date(date));
    // `fetchPreviews` can be called with not every assigned timeline items, but we want to pick closes expiration date
    //  even of those items that didn't get in current `fetchPreviews` call. So we store `closestExpirationDate`
    //  in the ref to make sure we invalidate previewURLs when the first previewURL (among all URLs that were loaded) expires.
    if (closestExpirationDate.current) expirationDates.push(closestExpirationDate.current);
    closestExpirationDate.current = sortDatesAsc(expirationDates)[0];

    // `closestExpirationDate.current` can be undefined in case if `fetchPreviewURLsUtil` didn't manage successfully fetch any previewURLs
    if (closestExpirationDate.current) {
      const timeout = closestExpirationDate.current.getTime() - new Date().getTime() - 60_000; // we clear previewURLs 1 minute before they actually expire "just in case", to make sure we don't feed stale urls to player.

      clearTimeout(resetPreviewUrlsTimer.current);
      resetPreviewUrlsTimer.current = setTimeout(() => {
        // Resetting preview URL will make segments and overlays change, which will trigger useEffect callback that will call `fetchPreviews` again
        //  so previewURLs will be automatically reloaded after `resetAllPreviewUrls` action dispatched
        dispatch(resetAllPreviewUrls(undefined, { excludeFromHistory: true }));
        resetPreviewUrlsTimer.current = undefined;
      }, timeout);
    }
  }, []);

  const fetchPreviews = useCallback(
    (items: TimelineItem[]) => {
      setIsLoading(true);
      fetchPreviewURLsUtil(items)
        .then((previewURLsBySegmentId) => {
          // The reason why we want to set previewURLs in batch, instead of setting them one by one as soon as any of
          // them is loaded, is because when segments change we re-initialize video player, and we don't to do it
          // every time any individual previewURL is loaded.
          dispatch(setPreviewUrls(previewURLsBySegmentId, { excludeFromHistory: true }));
          return previewURLsBySegmentId;
        })
        .then(setResetPreviewsTimeout)
        .finally(() => {
          setIsLoading(false);
        });
    },
    [setResetPreviewsTimeout]
  );

  useEffect(() => {
    if (!isPlayerOpened) return;

    if (!isLoading && !areAllAssignedItemsPreviewsLoaded(segments, overlaysFlat)) {
      fetchPreviews([...segments, ...overlaysFlat]);
    }
  }, [segments, overlaysFlat, isPlayerOpened, isLoading, setResetPreviewsTimeout]);

  useEffect(() => () => clearTimeout(resetPreviewUrlsTimer.current), []);

  const retryLoadingFailedPreview = useCallback(() => {
    const failedPreviewItems = [...segments, ...overlaysFlat]
      .filter((timelineItem) => {
        return isTimelineItemAssigned(timelineItem);
      })
      .filter((assignedItem) => Boolean(assignedItem.source.previewLoadingError));

    fetchPreviews(failedPreviewItems);
  }, [segments, overlaysFlat, setResetPreviewsTimeout]);

  return children(isLoading, retryLoadingFailedPreview);
};

export default PreviewUrlLoader;
